import React from 'react';
import { useLang } from '@/i18n/LanguageContext';
import PageContainer from '@/components/PageContainer';
import PageHeader from '@/components/PageHeader';
import { FIRM_INFO } from '@/lib/assets';
import { MapPin, Phone, Mail, ExternalLink } from 'lucide-react';

export default function Contact() {
  const { t } = useLang();
  const c = t.contact;

  return (
    <PageContainer>
      <PageHeader kicker={c.kicker} title={c.title} lead={c.intro} />

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-5 gap-10">
        {/* Info */}
        <div className="lg:col-span-2 space-y-8 animate-fade-up">
          {/* Address */}
          <div data-testid="contact-address">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-navy text-[var(--color-gold)] flex-shrink-0">
                <MapPin size={18} />
              </div>
              <div>
                <p className="text-[10px] tracking-[0.35em] uppercase text-[var(--color-gold)] font-semibold mb-2">
                  {c.addressLabel}
                </p>
                <p className="font-display text-xl text-navy leading-snug">
                  Cabinet VALTHELIS Avocats
                </p>
                <p className="text-navy/80 mt-1 leading-relaxed">
                  5 Place de l’Abbé Boursier
                  <br />
                  69370 Saint Didier au Mont d’Or
                </p>
              </div>
            </div>
          </div>

          {/* Phone */}
          <div data-testid="contact-phone">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-navy text-[var(--color-gold)] flex-shrink-0">
                <Phone size={18} />
              </div>
              <div>
                <p className="text-[10px] tracking-[0.35em] uppercase text-[var(--color-gold)] font-semibold mb-2">
                  {c.phoneLabel}
                </p>
                <a
                  href={`tel:${FIRM_INFO.phoneLink}`}
                  className="font-display text-xl text-navy hover:text-[var(--color-gold)] transition-colors"
                  data-testid="phone-link"
                >
                  {FIRM_INFO.phone}
                </a>
              </div>
            </div>
          </div>

          {/* Emails */}
          <div data-testid="contact-emails">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-navy text-[var(--color-gold)] flex-shrink-0">
                <Mail size={18} />
              </div>
              <div className="flex-1">
                <p className="text-[10px] tracking-[0.35em] uppercase text-[var(--color-gold)] font-semibold mb-3">
                  {c.emailsLabel}
                </p>
                <ul className="space-y-3">
                  <li>
                    <a
                      href={`mailto:${FIRM_INFO.emails.secretariat}`}
                      className="group flex flex-col"
                      data-testid="email-secretariat"
                    >
                      <span className="text-[11px] uppercase tracking-[0.2em] text-[var(--color-taupe)]">
                        Secrétariat
                      </span>
                      <span className="text-navy group-hover:text-[var(--color-gold)] transition-colors">
                        {FIRM_INFO.emails.secretariat}
                      </span>
                    </a>
                  </li>
                  <li>
                    <a
                      href={`mailto:${FIRM_INFO.emails.laurence}`}
                      className="group flex flex-col"
                      data-testid="email-laurence"
                    >
                      <span className="text-[11px] uppercase tracking-[0.2em] text-[var(--color-taupe)]">
                        Laurence VERDIER
                      </span>
                      <span className="text-navy group-hover:text-[var(--color-gold)] transition-colors">
                        {FIRM_INFO.emails.laurence}
                      </span>
                    </a>
                  </li>
                  <li>
                    <a
                      href={`mailto:${FIRM_INFO.emails.ageron}`}
                      className="group flex flex-col"
                      data-testid="email-ageron"
                    >
                      <span className="text-[11px] uppercase tracking-[0.2em] text-[var(--color-taupe)]">
                        Jean-Michel AGERON
                      </span>
                      <span className="text-navy group-hover:text-[var(--color-gold)] transition-colors">
                        {FIRM_INFO.emails.ageron}
                      </span>
                    </a>
                  </li>
                  <li>
                    <a
                      href={`mailto:${FIRM_INFO.emails.caroline}`}
                      className="group flex flex-col"
                      data-testid="email-caroline"
                    >
                      <span className="text-[11px] uppercase tracking-[0.2em] text-[var(--color-taupe)]">
                        Caroline BILLOTET
                      </span>
                      <span className="text-navy group-hover:text-[var(--color-gold)] transition-colors">
                        {FIRM_INFO.emails.caroline}
                      </span>
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Map */}
        <div className="lg:col-span-3 animate-fade-up" data-testid="contact-map">
          <p className="text-[10px] tracking-[0.35em] uppercase text-[var(--color-gold)] font-semibold mb-4">
            {c.mapLabel}
          </p>
          <div className="relative aspect-[4/3] lg:aspect-auto lg:h-[480px] w-full overflow-hidden border border-[var(--color-taupe)]/20 shadow-md">
            <iframe
              title="Cabinet VALTHELIS Avocats - Google Maps"
              src={FIRM_INFO.mapsEmbedUrl}
              className="w-full h-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
          <a
            href={FIRM_INFO.mapsLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-4 text-[12px] tracking-[0.25em] uppercase text-navy hover:text-[var(--color-gold)] border-b border-[var(--color-gold)] pb-1 transition-colors"
            data-testid="open-in-maps"
          >
            <ExternalLink size={14} />
            {c.openInMaps}
          </a>
        </div>
      </div>
    </PageContainer>
  );
}
